import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import { Theme } from "./Theme";

type ToastType = "error" | "success" | "warning" | "info";

const DEFAULT_TOAST_DURATION_MS = 4000;
const MAX_TIMEOUT_MS = 2147483647;

type ToastItem = {
  id: number;
  type: ToastType;
  message: string;
};

type ShowToastParams = {
  type?: ToastType;
  message: string;
  /** Display duration in milliseconds. Invalid values use the default. */
  duration?: number;
};

type ToastContextType = {
  showToast: (params: ShowToastParams) => void;
  hideToast: (id: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(1);
  const toastsRef = useRef<ToastItem[]>([]);
  const timeoutIdsRef = useRef<Map<number, number>>(new Map());

  const hideToast = useCallback((id: number) => {
    const timeoutId = timeoutIdsRef.current.get(id);

    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutIdsRef.current.delete(id);
    }

    setToasts((prev) => {
      const nextToasts = prev.filter((item) => item.id !== id);
      toastsRef.current = nextToasts;
      return nextToasts;
    });
  }, []);

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;

    return () => {
      timeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeoutIds.clear();
    };
  }, []);

  const showToast = useCallback(
    ({ type = "info", message, duration }: ShowToastParams) => {
      const durationMs =
        typeof duration === "number" &&
        Number.isFinite(duration) &&
        duration > 0 &&
        duration <= MAX_TIMEOUT_MS
          ? duration
          : DEFAULT_TOAST_DURATION_MS;
      const existingToast = toastsRef.current.find(
        (item) => item.type === type && item.message === message
      );
      const toastId = existingToast?.id ?? idRef.current++;

      setToasts((prev) => {
        const matchingToast = prev.find(
          (item) => item.type === type && item.message === message
        );
        const nextToasts = matchingToast
          ? prev
          : [...prev, { id: toastId, type, message }];

        toastsRef.current = nextToasts;
        return nextToasts;
      });

      const existingTimeoutId = timeoutIdsRef.current.get(toastId);

      if (existingTimeoutId !== undefined) {
        window.clearTimeout(existingTimeoutId);
      }

      const timeoutId = window.setTimeout(() => {
        hideToast(toastId);
      }, durationMs);

      timeoutIdsRef.current.set(toastId, timeoutId);
    },
    [hideToast]
  );

  const value = useMemo(
    () => ({
      showToast,
      hideToast,
    }),
    [showToast, hideToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {typeof document !== "undefined" &&
        createPortal(
          <StyledToastViewport>
            <AnimatePresence initial={false}>
              {toasts.map((toast) => (
                <StyledToastItem
                  key={toast.id}
                  $type={toast.type}
                  initial={{ opacity: 0, y: -22, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -22, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  <StyledToastInner>
                    <StyledToastDot $type={toast.type} />
                    <StyledToastText>{toast.message}</StyledToastText>
                  </StyledToastInner>
                </StyledToastItem>
              ))}
            </AnimatePresence>
          </StyledToastViewport>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }

  return context;
};

const StyledToastViewport = styled.div`
  position: fixed;
  top: max(14px, env(safe-area-inset-top));
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100vw - 24px), 430px);
  z-index: 9999;
  pointer-events: none;

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledToastItem = styled(motion.div)<{ $type: ToastType }>`
  pointer-events: auto;
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(14px);

  background: ${({ $type }) =>
    $type === "error"
      ? `
        linear-gradient(180deg, rgba(255, 107, 107, 0.14) 0%, rgba(255, 107, 107, 0.08) 100%),
        linear-gradient(195deg, #171011 0%, #120d0d 100%)
      `
      : $type === "success"
      ? `
        linear-gradient(180deg, rgba(57, 217, 138, 0.14) 0%, rgba(57, 217, 138, 0.08) 100%),
        linear-gradient(195deg, #101713 0%, #0d120f 100%)
      `
      : $type === "warning"
      ? `
        linear-gradient(180deg, rgba(255, 184, 77, 0.14) 0%, rgba(255, 184, 77, 0.08) 100%),
        linear-gradient(195deg, #17130e 0%, #120f0b 100%)
      `
      : `
        linear-gradient(180deg, rgba(${Theme.colors.accentRgb}, 0.14) 0%, rgba(${Theme.colors.accentRgb}, 0.08) 100%),
        linear-gradient(195deg, #0f1417 0%, #0c1012 100%)
      `};

  border: 1px solid
    ${({ $type }) =>
      $type === "error"
        ? Theme.colors.dangerBorderColor
        : $type === "success"
        ? Theme.colors.successBorderColor
        : $type === "warning"
        ? Theme.colors.warningBorderColor
        : `rgba(${Theme.colors.accentRgb}, 0.18)`};

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 26px rgba(0, 0, 0, 0.20);
`;

const StyledToastInner = styled.div`
  min-height: 52px;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledToastDot = styled.div<{ $type: ToastType }>`
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 999px;
  background: ${({ $type }) =>
    $type === "error"
      ? Theme.colors.dangerColor
      : $type === "success"
      ? Theme.colors.successColor
      : $type === "warning"
      ? Theme.colors.warningColor
      : Theme.colors.accentColor};

  box-shadow: 0 0 14px
    ${({ $type }) =>
      $type === "error"
        ? "rgba(255, 107, 107, 0.35)"
        : $type === "success"
        ? "rgba(57, 217, 138, 0.35)"
        : $type === "warning"
        ? "rgba(255, 184, 77, 0.35)"
        : `rgba(${Theme.colors.accentRgb}, 0.35)`};
`;

const StyledToastText = styled.div`
  font-size: ${Theme.font.fontSize3};
  line-height: 1.4;
  font-weight: ${Theme.font.fontWeight2};
  color: ${Theme.colors.mainFontColor};
  word-break: break-word;
`;
